import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';

import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { mapToProperty, whenTruthy } from 'ish-core/utils/operators';

import { OrderTemplatesFacade } from '../../facades/order-templates.facade';
import { OrderTemplate, OrderTemplateItem } from '../../models/order-template/order-template.model';

interface SkuCheck {
  sku: string;
  checked: boolean;
}

@Component({
  selector: 'ish-account-order-template-detail-page',
  templateUrl: './account-order-template-detail-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountOrderTemplateDetailPageComponent implements OnInit, OnDestroy {
  orderTemplate$: Observable<OrderTemplate>;
  orderTemplateError$: Observable<HttpError>;
  orderTemplateLoading$: Observable<boolean>;

  noOfUnavailableProducts$: Observable<number>;

  isAddToCartDisabled = false;
  showAddToCartButton$ = new BehaviorSubject<boolean>(true);
  private skuCheckList: SkuCheck[] = [];
  private checkedArray: SkuCheck[] = [];
  private destroy$ = new Subject<void>();

  constructor(private orderTemplatesFacade: OrderTemplatesFacade) {}

  ngOnInit() {
    this.orderTemplate$ = this.orderTemplatesFacade.getOrderTemplateById$();
    this.orderTemplateLoading$ = this.orderTemplatesFacade.orderTemplateLoading$;
    this.orderTemplateError$ = this.orderTemplatesFacade.orderTemplateError$;
    this.noOfUnavailableProducts$ = this.orderTemplatesFacade.currentOrderTemplateOutOfStockItems$.pipe(
      mapToProperty('length')
    );

    combineLatest([this.orderTemplate$, this.orderTemplatesFacade.currentOrderTemplateOutOfStockItems$])
      .pipe(whenTruthy(), debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(([orderTemplateData, outOfStockItems]) => {
        if (orderTemplateData) {
          const allSkus = orderTemplateData.items.map(item => item.sku);
          const availableSkusArray = allSkus.filter(item => outOfStockItems.indexOf(item) < 0);
          availableSkusArray.forEach(item => this.skuCheckList.push({ sku: item, checked: true }));

          this.removeDuplicateSkus();

          this.updateButtonStatus();
        }
      });
  }

  editPreferences(orderTemplate: OrderTemplate, orderTemplateName: string) {
    this.orderTemplatesFacade.updateOrderTemplate({
      ...orderTemplate,
      id: orderTemplateName,
    });
  }

  trackByFn(_: number, item: OrderTemplateItem) {
    return item.id;
  }

  removeDuplicateSkus() {
    this.skuCheckList = this.skuCheckList.filter(
      //finding the index by checking if the sku of self and sku of item is same
      // if the found index of self and current index of skuCheckList is same then this sku item will be filtered
      (item, index, self) => index === self.findIndex(t => t.sku === item.sku)
    );
  }

  updateButtonStatus() {
    this.checkedArray = this.skuCheckList.filter(item => item.checked);
    this.isAddToCartDisabled = this.checkedArray.length === 0;
  }

  setAddToCartButtonStatus(checkBoxEvent: boolean, sku: string) {
    const index = this.skuCheckList.findIndex(item => item.sku === sku);
    if (index !== -1) {
      this.skuCheckList[index].checked = checkBoxEvent;
      this.updateButtonStatus();
    }
  }

  removeDeletedSkuFromSkuCheckList(sku: string) {
    //filtering checked sku that is removed
    const checkedSkuRemoved = this.skuCheckList.filter(item => item.checked && item.sku === sku);

    //disabling add to cart button when the last checked sku is deleted
    if (checkedSkuRemoved) {
      const remainingCheckedSkus = this.skuCheckList.filter(item => item.checked && item.sku !== sku);
      this.isAddToCartDisabled = remainingCheckedSkus.length === 0;
    }

    this.skuCheckList = this.skuCheckList.filter(item => item.sku !== sku);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
